<script>
import draggable from "vuedraggable";
import {centerLatLng, initialMapOptions} from "../include/Map";

const ALPHABET = "ABCDEFGHIJKLMNOPQRSTUVWXYZ";

export default {
    name: "JobRoutesMapButton",
    components: {draggable},
    props: {
        events: {
            type: Array,
            required: true,
        },
        user: {
            type: Object,
            required: true,
        },
        engineerMode: {
            type: Boolean,
            required: false,
            default: false,
        },
    },
    data: function () {
        return {
            orderedEvents: [],
            showModal: false,
            map: undefined,
            directionsResponse: undefined,
            alphabet: ALPHABET.split(''),
        };
    },
    computed: {
        headquarters() {
            const settings = window.SystemSettings || {};
            const lat = settings.address_lat;
            const lng = settings.address_lng;
            if (lat === null || lat === undefined || lat === '') {
                return null;
            }
            if (lng === null || lng === undefined || lng === '') {
                return null;
            }
            return {
                lat: parseFloat(lat),
                lng: parseFloat(lng),
                address: settings.address || '',
                label: 'Headquarters',
            };
        },
        hasHeadquarters() {
            return !!this.headquarters;
        },
        initialCenter() {
            if (this.headquarters) {
                return {
                    lat: this.headquarters.lat,
                    lng: this.headquarters.lng,
                };
            }
            if (!this.events[0]) {
                return centerLatLng;
            }
            const address = this.orderedEvents[0] && this.orderedEvents[0].job.site.address;
            if (!address || !address.lat || !address.lng) {
                return undefined;
            }

            return {
                lat: address.lat,
                lng: address.lng,
            };
        },
    },
    watch: {
        orderedEvents: {
            handler() {
                this.initialiseMap();
            },
            immediate: true,
        },
    },
    methods: {
        toggleShowModal() {
            this.showModal = !this.showModal;
            if (this.showModal) {
                this.orderedEvents = [...this.events];
                return
            }
            this.map = undefined;
        },
        initialiseMap() {
            window.setTimeout(() => {
                if (!this.showModal) {
                    return;
                }

                this.map = new window.google.maps.Map(document.getElementById('job-route-map'), initialMapOptions);

                const jobMarkers = this.orderedEvents.map((event, index) => {
                    const address = event.job.site.address;
                    if (!address || !address.lat || !address.lng) {
                        return undefined;
                    }

                    const coord = {
                        lat: address.lat,
                        lng: address.lng,
                    };

                    return new window.google.maps.Marker({
                        position: coord,
                        map: this.map,
                        title: `${event.job.customer.company_name} ${event.start.format('Do MMM H:mm A')}`,
                        icon: {
                            path: window.google.maps.SymbolPath.CIRCLE,
                            fillColor: event.backgroundColor,
                            fillOpacity: 1,
                            strokeColor: event.textColor,
                            strokeWeight: 1,
                            scale: 12,
                        },
                        label: this.jobLetter(index),
                    });
                }).filter(marker => !!marker);

                const routePositions = [];
                if (this.headquarters) {
                    const hqCoord = {
                        lat: this.headquarters.lat,
                        lng: this.headquarters.lng,
                    };
                    new window.google.maps.Marker({
                        position: hqCoord,
                        map: this.map,
                        title: 'Headquarters',
                        icon: {
                            url: this.headquartersMarkerIconUrl(),
                            scaledSize: new window.google.maps.Size(36, 36),
                            anchor: new window.google.maps.Point(18, 36),
                        },
                    });
                    routePositions.push(hqCoord);
                }
                jobMarkers.forEach(marker => {
                    routePositions.push(marker.getPosition());
                });

                if (routePositions.length < 2) {
                    return;
                }

                const directionsService = new window.google.maps.DirectionsService();
                const directionsRenderer = new window.google.maps.DirectionsRenderer({
                    suppressMarkers: true,
                });
                directionsRenderer.setMap(this.map);

                const start = routePositions[0];
                const end = routePositions[routePositions.length - 1];
                const waypoints = routePositions.slice(1, routePositions.length - 1).map(position => ({
                    location: position,
                    stopover: true,
                }));

                const request = {
                    origin: start,
                    destination: end,
                    waypoints: waypoints,
                    travelMode: window.google.maps.TravelMode.DRIVING,
                    unitSystem: window.google.maps.UnitSystem.IMPERIAL,
                };

                directionsService.route(request, (response, status) => {
                    if (status === window.google.maps.DirectionsStatus.OK) {
                        this.directionsResponse = response;
                        directionsRenderer.setDirections(response);
                    } else {
                        alert('Directions request failed due to ' + status);
                    }
                });
            }, 500);
        },

        getLeg(index) {
            if (
                !this.directionsResponse
                || !this.directionsResponse.routes[0]
                || !this.directionsResponse.routes[0].legs[index]
            ) {
                return undefined;
            }

            return this.directionsResponse.routes[0].legs[index];
        },
        jobLetter(index) {
            return this.alphabet[index];
        },
        legForJob(index) {
            return this.getLeg(this.hasHeadquarters ? index + 1 : index);
        },
        headquartersMarkerIconUrl() {
            const svg = [
                '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 576 512">',
                '<path fill="#333333" d="M280.37 148.26L96 300.11V464a16 16 0 0 0 16 16l112.06-.29a16 16 0 0 0 15.92-16V368a16 16 0 0 1 16-16h64a16 16 0 0 1 16 16v95.64a16 16 0 0 0 16 16.05L464 480a16 16 0 0 0 16-16V300L295.67 148.26a12.19 12.19 0 0 0-15.3 0zM571.6 251.47L488 182.56V44.05a12 12 0 0 0-12-12h-56a12 12 0 0 0-12 12v72.61L318.47 43a48 48 0 0 0-61 0L4.34 251.47a12 12 0 0 0-1.6 16.9l25.5 31A12 12 0 0 0 45.15 301l235.22-193.74a12.19 12.19 0 0 1 15.3 0L530.9 301a12 12 0 0 0 16.9-1.6l25.5-31a12 12 0 0 0-1.7-16.93z"/>',
                '</svg>',
            ].join('');

            return 'data:image/svg+xml;charset=UTF-8,' + encodeURIComponent(svg);
        },

        storeOrder() {
            const time_slot_ids = this.orderedEvents.map(timeSlot => timeSlot.id);
            if (!time_slot_ids.length) {
                return;
            }

            window.axios.post('/api/timeslots/order', {
                time_slot_ids,
            }).then();
        },
    },
}
</script>

<template>
    <div>
        <div class="text-center">
            <button
                v-if="events.length > 0 && !engineerMode"
                class="btn btn-secondary view-map-btn"
                style="margin-top: .5rem"
                @click.prevent="toggleShowModal"
            >
                <i class="fas fa-car" />
                <span>View Routes & Order</span>
                <i class="fas fa-sort" />
            </button>
            <button
                v-if="events.length > 0 && engineerMode"
                class="btn floating-routes-btn"
                @click.prevent="toggleShowModal"
            >
                <i
                    class="fas fa-car"
                />
            </button>
        </div>
        <div
            v-if="showModal"
            class="modal"
        >
            <div
                class="modal__background"
                @click="toggleShowModal"
            />
            <div class="modal__foreground">
                <div class="modal__body">
                    <div class="modal__header">
                        <h2 class="modal-title">
                            Job routes for <span>{{ user.name }}</span> on
                            <span>{{ events[0].start.format('Do MMMM YYYY') }}</span>
                        </h2>
                        <button
                            class="modal__close"
                            @click.prevent="toggleShowModal"
                        >
                            <i class="fa fa-times" />
                        </button>
                    </div>
                    <div class="modal-row row">
                        <div class="col-md-9">
                            <div
                                id="job-route-map"
                            />
                        </div>
                        <div class="col-md-3">
                            <div
                                v-if="hasHeadquarters"
                                class="job-card hq-card"
                            >
                                <div class="job-card-heading">
                                    <div>
                                        <h3>
                                            <i class="fas fa-home" />
                                            Headquarters
                                        </h3>
                                    </div>
                                </div>
                                <div>
                                    {{ headquarters.address }}
                                </div>
                            </div>
                            <div
                                v-if="hasHeadquarters && getLeg(0)"
                                class="leg-info"
                            >
                                <i class="fas fa-car" />
                                <span>
                                    {{ getLeg(0).distance.text }} - {{ getLeg(0).duration.text }}
                                </span>
                            </div>
                            <div
                                v-if="!hasHeadquarters"
                                class="hq-warning"
                            >
                                <strong>Headquarters</strong> is not configured.
                                Add the company address and map pin in System Settings.
                            </div>
                            <draggable
                                v-model="orderedEvents"
                                handle=".drag-handle"
                                @change="storeOrder"
                            >
                                <div
                                    v-for="(event, index) in orderedEvents"
                                    :key="event.id"
                                >
                                    <div
                                        :style="{'background-color': event.backgroundColor, 'color': event.textColor, 'border-color': event.borderColor}"
                                        class="job-card"
                                    >
                                        <div class="job-card-heading">
                                            <div>
                                                <h3>({{ jobLetter(index) }}) {{ event.job.customer.company_name }}</h3>
                                                <h4>{{ event.start.format('Do MMM YYYY') }}</h4>
                                            </div>
                                            <span
                                                v-if="!engineerMode"
                                                class="drag-handle"
                                            >
                                                <i class="fas fa-arrows-alt-v" />
                                            </span>
                                        </div>
                                        <div>
                                            {{ event.job.site.address.formatted }}
                                        </div>
                                    </div>
                                    <div
                                        v-if="legForJob(index)"
                                        class="leg-info"
                                    >
                                        <i class="fas fa-car" />
                                        <span>
                                            {{ legForJob(index).distance.text }}
                                            -
                                            {{ legForJob(index).duration.text }}
                                        </span>
                                    </div>
                                </div>
                            </draggable>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<style scoped lang="scss">
.view-map-btn {
    font-size: .75rem;
    width: 98%;
    background-color: #4b4b4b;
    margin: 2px;
}

.modal__background {
    background-color: rgba(0, 0, 0, 0.2);
}

.modal__body {
    height: 88vh;
    max-height: 88vh;

    @media screen and (min-width: 769px) {
        width: 80vw;
        height: 80vh;
    }
}

.modal-title span {
    color: #c06212;
}

.modal-row {
    height: 100%;
}

#job-route-map {
    width: 100%;
    height: 100%;
    max-width: 100%;
}

.job-card {
    margin: 1rem 0;
    border-radius: .5rem;
    padding: 1rem !important;
}

.hq-card {
    background: #f3f3f3;
    color: #222;
    border: 1px solid #ccc;
}

.hq-warning {
    margin: 1rem 0;
    padding: 0.75rem;
    background: #fff3cd;
    border-radius: 0.5rem;
    font-size: 0.9rem;
}

.job-card-heading {
    display: flex;
    justify-content: space-between;
}

.drag-handle {
    max-height: max-content;
}

.leg-info {
    text-align: center;
    padding: .5rem;
    background: #e2e2e2;
    border-radius: .5rem;
}

#job-route-map {
    min-height: 40vh;
}
</style>


// WEBPACK FOOTER //
// resources/assets/js/components/JobRoutesMapButton.vue